import { defineStore } from "pinia";

import type { BoardType, IdMap } from "@/model/baseTypes";
import { isFlexBoard } from "@/model/board";
import type { AlmStickyType, StickyType } from "@/model/stickyType";
import { useGlobalI18n } from "@/translations/i18n";

const { t } = useGlobalI18n();

export const useStickyTypeStore = defineStore("stickyType", {
  state: () => ({
    stickyTypes: [] as StickyType[],
    almStickyTypes: {} as IdMap<AlmStickyType>,
  }),
  getters: {
    findStickyType:
      (state) =>
      (id: string): StickyType => {
        const type = state.stickyTypes.find((t) => t.id === id);
        return type || zombieStickyType();
      },
    findTypeByAlmType:
      (state) =>
      (almTypeId: string): StickyType =>
        state.stickyTypes.find((t) => t.almType === almTypeId) || zombieStickyType(),
    nonFlexBoardStickyTypes: (state) => (type: BoardType) => {
      const originTypes = state.stickyTypes.filter((s) => s.origin === type);
      const mirrorTypes = state.stickyTypes.filter(
        (s) => s.origin !== type && s.usable.find((a) => a === type),
      );
      return boardStickyTypes(originTypes, mirrorTypes);
    },
    flexBoardStickyTypes: (state) => (flexType: string) => {
      const originTypes = state.stickyTypes.filter(
        (s) => isFlexBoard(s.origin) && s.flexOrigin === flexType,
      );
      const mirrorTypes = state.stickyTypes.filter(
        (s) =>
          !isFlexBoard(s.origin) &&
          s.usable.find(isFlexBoard) &&
          (s.flexUsable || []).find((a) => a === flexType),
      );
      return boardStickyTypes(originTypes, mirrorTypes);
    },
    almStickyTypeOf: (state) => {
      return (stickyType: Pick<StickyType, "almType">) =>
        state.almStickyTypes[stickyType.almType || ""];
    },
  },
  actions: {
    /**
     * Maps sticky type IDs to their corresponding ALM (e.g., Jira) issue type IDs.
     * Filters out any sticky types that don't have an associated ALM type.
     *
     * @param stickyTypeIds - Array of sticky type IDs to map
     * @returns Array of ALM type IDs, or empty array if no valid mappings exist
     */
    getAlmTypeIds(stickyTypeIds: string[]): string[] {
      return stickyTypeIds
        .map((id) => this.stickyTypes.find((t) => t.id === id)?.almType)
        .filter((almType): almType is string => !!almType);
    },
    addStickyType(stickyType: StickyType) {
      this.stickyTypes.push(stickyType);
    },
    updateStickyType(stickyType: StickyType) {
      const i = this.stickyTypes.findIndex((type) => type.id === stickyType.id);
      if (i >= 0) {
        this.stickyTypes[i] = stickyType;
      }
    },
    removeStickyType(id: string) {
      this.stickyTypes = this.stickyTypes.filter((type) => type.id !== id);
    },
  },
});

function boardStickyTypes(origin: StickyType[], mirror: StickyType[]) {
  mirror.sort((a, b) => a.name.localeCompare(b.name));
  return [...origin, ...mirror];
}

function zombieStickyType(): StickyType {
  return {
    id: "0",
    name: t("stickyType.unknown"),
    almType: null,
    color: [0.5, 0.5, 0.5, 0.8],
    altColor: [0.5, 0.5, 0.5, 0.8],
    origin: "risk",
    flexOrigin: null,
    usable: [],
    flexUsable: [],
    functionality: "note",
  };
}

export function isZombieType(type: StickyType) {
  return type.id === "0";
}

export function isStickyTypeAlmSynced(type: StickyType) {
  return useStickyTypeStore().almStickyTypeOf(type)?.isMapped;
}
